import "./DataTemplateSelector.less";

import { Button, Modal } from "antd";
import arrayMove from "array-move";
import React from "react";

import { DataFieldApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { colorList } from "lib/colors";
import { IBaseProps } from "lib/data/interfaces";
import { DataField } from "lib/data/model";
import { DataFieldTemplate, MetadataTemplate, TextDataField } from "lib/data/model";
import { MultipleItemRequirement, Requirement, RequirementItem, SingleItemRequirement } from "lib/data/model";
import { Requiredness } from "lib/data/model/enumerations";
import { StateService } from "lib/services";

import { TemplateCard } from "components/general";
import { actionTypes } from "components/general/TemplateCard/ActionTypes";
import { IconType } from "lib/data/model/dictionaries/IconTypes";
import { withRouter, RouteComponentProps } from "react-router";
import { MultipleItemConfiguration } from "../../TemplateHierarchy/components/MultipleItemConfiguration";
import { NewRequirement } from "../../TemplateHierarchy/components/NewRequirement";
import { NewMultiItemRequirementDataField } from "../NewMultiItemRequirementDataField";
import { NewSingleItemRequirementDataField } from "../NewSingleItemRequirementDataField";
import { SortableContainer } from "../TemplateRequirementSelector/SortableContainer";
import { SortableItem } from "../TemplateRequirementSelector/SortableItem";

const currentMultiItemIds: Map<string> = {};

interface Map<T> {
	[K: string]: T;
}

interface IDataTemplateSelectorProps extends IBaseProps, RouteComponentProps<any> {
	metadataTemplate: MetadataTemplate;
	waitingForUpdate: boolean;
	softSectionTemplateUpdate: () => void;
	hardSectionTemplateUpdate: (callback?: () => void) => void;

}

interface IDataTemplateSelectorState {
	selectingNewItem: boolean;
	selectingNewItems: boolean;
}



export class DataTemplateSelectorPreRouter extends React.Component<IDataTemplateSelectorProps, IDataTemplateSelectorState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();

	private dataTemplateRequirementItems: Array<RequirementItem<DataFieldTemplate>> = [];
	private dataFieldTypes: DataField[] = [];
	private multipleItemRequirement?: MultipleItemRequirement<DataFieldTemplate>;

	constructor(props: IDataTemplateSelectorProps) {
		super(props);
		this.state = {
			selectingNewItem: false,
			selectingNewItems: false
		};
	}

	public componentWillMount = async () => {
		this.setupDataTemplateRequirements(this.props);
		this.setupDataFieldTypes();
	}

	public componentWillUpdate = async (nextProps: IDataTemplateSelectorProps) => {
		if (nextProps.metadataTemplate.requirements !== this.props.metadataTemplate.requirements) {
			this.setupDataTemplateRequirements(nextProps);
			this.setupDataFieldTypes();
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {
		return (
			<div className="data-template-selector">
				<SortableContainer
					onSortEnd={this.onTemplateDrop}
					axis="xy" >
					<NewRequirement className="non-sortable-item" newMultipleItemClick={this.newMuiltiItemRequirementClick} newSingleItemClick={this.newSingleItemRequirementClick} />
					{/* <NewSingleItemRequirement className="non-sortable-item" newSingleItemClick={this.newSingleItemRequirementClick} /> */}
					{this.renderTemplateData()}
				</SortableContainer>
				<NewSingleItemRequirementDataField
					dataFieldTypes={this.dataFieldTypes}
					waitingForUpdate={this.props.waitingForUpdate}
					addNewDataTemplateRequirement={this.addNewDataTemplateRequirement}
					visible={this.state.selectingNewItem}
					closeModal={this.closeSingleItemModal}
				/>
				<NewMultiItemRequirementDataField
					dataFieldTypes={this.dataFieldTypes}
					waitingForUpdate={this.props.waitingForUpdate}
					addNewMultiItemRequirement={this.addNewDataTemplateRequirements}
					visible={this.state.selectingNewItems}
					closeModal={this.closeMultipleItemsModal}
				/>
				<Modal
					footer={[
						<Button type="primary" icon="save" onClick={this.saveMultipleItemUpdate}>save</Button>
					]}
					visible={this.multipleItemRequirement !== undefined} onCancel={this.closeMultipleItemUpdate}>
					<div className="configuration-screen">
						{this.multipleItemRequirement !== undefined
							? <MultipleItemConfiguration updateMultiItemRequirement={this.updateMultiItemRequirement} multipleItemRequirement={this.multipleItemRequirement} />
							: null}
					</div>
				</Modal>
			</div>
		);
	}

	private setupDataFieldTypes = async () => {
		const dataFieldTypes: DataField[] = await DataFieldApi.getDataFields();
		this.dataFieldTypes = dataFieldTypes.sort((dataFieldA, dataFieldB) => {
			if (dataFieldA.name > dataFieldB.name) { return 1; }
			if (dataFieldA.name < dataFieldB.name) { return -1; }
			return 0;
		});
	}

	private setupDataTemplateRequirements = (props: IDataTemplateSelectorProps) => {
		// Extract all Requirement Items
		const requirementItems: Array<RequirementItem<DataFieldTemplate>> = [];
		props.metadataTemplate.requirements.forEach((requirement: Requirement) => {
			switch (requirement.$type) {
				case SingleItemRequirement.$type.DataFieldTemplate:
					// create a typed version of the requirement
					const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;
					// Setup parent Getter.
					singleItemRequirement.item.item.getParent = () => singleItemRequirement;
					// Push item out to requirement Items
					requirementItems.push(singleItemRequirement.item);
					break;
				case MultipleItemRequirement.$type.DataFieldTemplate:
					// create a typed version of the requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;
					// Push items out to requirement Items
					multipleItemRequirement.items.forEach(
						(requirementItem) => {
							requirementItem.item.getParent = () => multipleItemRequirement;
							requirementItems.push(requirementItem);
						}
					);
					break;

				default:
					break;
			}
		});
		this.dataTemplateRequirementItems = requirementItems.sort((a, b) => {
			const singleItemRequirementA = a as RequirementItem<DataFieldTemplate>;
			const singleItemRequirementB = b as RequirementItem<DataFieldTemplate>;
			return singleItemRequirementA.initialOrder - singleItemRequirementB.initialOrder;
		});

		this.forceUpdate();
	}

	private actionClickHandler = (command: actionTypes, index?: number) => {
		if (index !== undefined) {
			const requirementItem: RequirementItem<DataFieldTemplate> = this.dataTemplateRequirementItems[index];
			if (requirementItem.item.getParent) {
				const parentRequirement = requirementItem.item.getParent();
				switch (command) {
					case "delete":
						switch (parentRequirement.$type) {
							case SingleItemRequirement.$type.DataFieldTemplate:
								// Filter out requirement from list
								this.props.metadataTemplate.requirements = this.props.metadataTemplate.requirements.filter((requirement) => requirement.id !== parentRequirement.id);
								this.dataTemplateRequirementItems.splice(index, 1);
								break;
							case MultipleItemRequirement.$type.DataFieldTemplate:
								// Filter out requirement from list
								this.props.metadataTemplate.requirements = this.props.metadataTemplate.requirements.filter((requirement) => requirement.id !== parentRequirement.id);
								this.dataTemplateRequirementItems = this.dataTemplateRequirementItems.filter((requirementItem) => {
									if (requirementItem.item.getParent) {
										const currentParentRequirement = requirementItem.item.getParent();
										return currentParentRequirement.id !== parentRequirement.id;
									}
								});
								break;
							default:
								break;

						}

						break;
					case "edit":
						switch (parentRequirement.$type) {
							case SingleItemRequirement.$type.DataFieldTemplate:
								break;
							case MultipleItemRequirement.$type.DataFieldTemplate:
								const multiItemRequirement = parentRequirement as MultipleItemRequirement<DataFieldTemplate>;
								this.openMultipleItemUpdate(multiItemRequirement);
								break;
							default:
								break;

						}
						break;
					default:
						break;
				}
			}
			this.props.softSectionTemplateUpdate();
		}
	}

	private onTemplateDrop = async ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
		// if the section template is avalible and the old and new index are not the same
		if (this.dataTemplateRequirementItems && oldIndex !== newIndex) {
			// Adjust placement in Template Reuirements
			this.dataTemplateRequirementItems = arrayMove(this.dataTemplateRequirementItems, oldIndex, newIndex);
			// Assign new sort order on the item itself(modifies this.sectionTemplate.requirements[0].item.initialOrder)
			this.dataTemplateRequirementItems.forEach((requirementItem, index) => {
				requirementItem.initialOrder = index;
			});

			this.props.softSectionTemplateUpdate();
			// update all changes on the screen with this.sectionTemplate
		}
	}

	private renderTemplateData = () => {
		// map out all the items
		if (this.dataTemplateRequirementItems.length) {
			return (
				this.dataTemplateRequirementItems.map((requirementItem, key) => {
					if (requirementItem.id !== undefined) {
						let dataType;
						let fieldTitle;
						if (requirementItem.item.field.$type === "StringDataField") {
							fieldTitle = requirementItem.item.field.name;
							dataType = requirementItem.item.field.dataType.name;
						}
						else if (requirementItem.item.field.$type === "ListDataField") {
							fieldTitle = requirementItem.item.field.name;
							dataType = "Dropdown";
						}
						else if (requirementItem.item.field.$type === "AggregateDataField") {
							fieldTitle = requirementItem.item.field.name;
							dataType = "*Contains multiple input fields";
						}
						else if (requirementItem.item.field.$type === TextDataField.$type) {
							fieldTitle = requirementItem.item.field.name;
							dataType = "Text";
						}
						else {

							fieldTitle = requirementItem.item.field.name;
							dataType = requirementItem.item.field.dataType.name;
						}
						if (requirementItem.item.getParent) {
							const parentRequirement = requirementItem.item.getParent();

							if (parentRequirement.$type === MultipleItemRequirement.$type.DataFieldTemplate && parentRequirement.id) {
								if (parentRequirement.id) {
									if (currentMultiItemIds[parentRequirement.id] === null) {
										currentMultiItemIds[parentRequirement.id] = colorList[(parentRequirement.id % colorList.length) - 1];
									}
								}
								return (
									<SortableItem key={`item-${key}`} index={key} className="data-template-card-wrapper">
										<TemplateCard
											onCheckboxclick={this.props.softSectionTemplateUpdate}
											key={requirementItem.id}
											title={fieldTitle}
											requirement={requirementItem}
											multiItemRequirementId={parentRequirement.id}
											id={requirementItem.id}
											multiRequirementColor={currentMultiItemIds[parentRequirement.id]}
											actionbuttons={["delete", "edit"]}
											actionButtonClick={(command: actionTypes) => { this.actionClickHandler(command, key); }}
											showCheckboxes={true}
											iconType={IconType.get(requirementItem.item.field.$type)}
											templateType={""}
											templateSubType={dataType}
										/>
									</SortableItem >
								);
							}
						}
						return (
							<SortableItem key={`item-${key}`} index={key} className="data-template-card-wrapper">
								<TemplateCard
									onCheckboxclick={this.props.softSectionTemplateUpdate}
									key={requirementItem.id}
									title={fieldTitle}
									requirement={requirementItem}
									id={requirementItem.id}
									actionbuttons={["delete"]}
									actionButtonClick={(command: actionTypes) => { this.actionClickHandler(command, key); }}
									showCheckboxes={true}
									iconType={IconType.get(requirementItem.item.field.$type)}
									templateType={""}
									templateSubType={dataType}
								/>
							</SortableItem >
						);
					}
					else {
						return null;
					}
				})
			);
		} else {
			return null;
		}
	}

	private newSingleItemRequirementClick = async () => {
		StateService.setCancelableState(() => this.setState({ selectingNewItem: true }), this.apiCancellationToken);
	}

	private newMuiltiItemRequirementClick = async () => {
		StateService.setCancelableState(() => this.setState({ selectingNewItems: true }), this.apiCancellationToken);
	}

	private addNewDataTemplateRequirement = (dataField: DataField) => {
		// Create a new Requirement
		const singleItemRequirement: SingleItemRequirement<DataFieldTemplate> = new SingleItemRequirement("DataFieldTemplate");
		// Create a new dataFieldTemplate
		const dataFieldTemplate: DataFieldTemplate = new DataFieldTemplate();
		// Create a new Requirement Item
		const requirementItem: RequirementItem<DataFieldTemplate> = new RequirementItem("DataFieldTemplate");
		dataFieldTemplate.name = dataField.name;
		dataFieldTemplate.stringAppendage = "";
		dataFieldTemplate.stringPrefix = "";
		requirementItem.id = 0;
		requirementItem.initialOrder = this.dataTemplateRequirementItems.length + 1;
		requirementItem.owningRequirement = singleItemRequirement;
		singleItemRequirement.isRepeatable = false;
		singleItemRequirement.requiredness = Requiredness.OPTIONAL;
		// Add data Field to Data Field Template
		dataFieldTemplate.field = dataField;
		// Add data Field Template to Item
		requirementItem.item = dataFieldTemplate;
		// Add Item to Requirement
		singleItemRequirement.item = requirementItem;
		// Push requirements to requierments list
		this.dataTemplateRequirementItems.push(requirementItem);
		// populate metadataTemplate with the new list
		this.props.metadataTemplate.requirements.push(singleItemRequirement);
		// Trigger a hard update
		this.props.hardSectionTemplateUpdate(() => {
			// call back a force update and close the modal
			StateService.setCancelableState(() => this.setState({ selectingNewItem: false }), this.apiCancellationToken);
			this.forceUpdate();
		});
	}
	private addNewDataTemplateRequirements = (multiItemRequirement: MultipleItemRequirement<DataFieldTemplate>, callback: Function) => {
		// populate metadataTemplate with the new list
		this.props.metadataTemplate.requirements.push(multiItemRequirement);
		// Trigger a hard update
		this.props.hardSectionTemplateUpdate(() => {
			// call back a force update and close the modal
			this.forceUpdate();
			StateService.setCancelableState(() => this.setState({ selectingNewItems: false }), this.apiCancellationToken);
			callback();
		});
	}

	private closeSingleItemModal = () => {
		if (!this.props.waitingForUpdate) {
			StateService.setCancelableState(() => this.setState({ selectingNewItem: false }), this.apiCancellationToken);
		}
	}

	private closeMultipleItemsModal = () => {
		if (!this.props.waitingForUpdate) {
			StateService.setCancelableState(() => this.setState({ selectingNewItems: false }), this.apiCancellationToken);
		}
	}

	private updateMultiItemRequirement = () => {
		this.forceUpdate();
	}

	private openMultipleItemUpdate = (multipleItemRequirement: MultipleItemRequirement<DataFieldTemplate>) => {
		this.multipleItemRequirement = multipleItemRequirement;
		this.forceUpdate();
	}

	private saveMultipleItemUpdate = () => {
		this.props.softSectionTemplateUpdate();
		this.closeMultipleItemUpdate();
	}

	private closeMultipleItemUpdate = () => {
		this.multipleItemRequirement = undefined;
		this.forceUpdate();
	}
}

export const DataTemplateSelector = withRouter(DataTemplateSelectorPreRouter);
